/**
 * 发票详情与校验报告 —— 平台最关键的页面
 *
 * 布局与财务人员的核对习惯一致：**左边票面、右边报告**，
 * 逐项对照「OCR 识别出了什么 / 税务局返回了什么 / 哪里不一致」。
 *
 * 本页承载了四条业务硬约束，全部落在可见的交互上：
 *   1. 校验结果只做风险标记，初审仍需人工点「通过」，没有任何自动放行入口；
 *   2. 高风险发票的「推送 T+」按钮被禁用并说明原因，只能走「特例放行」（必填专项审批依据）；
 *   3. 任一校验项为「外部校验暂不可用 / 校验失败」时，页面顶部固定出现「人工核验并接管」入口，
 *      必须填写核验说明并勾选已核验项（文档七明确要求，不允许被忽略）；
 *   4. T+ 凭证号只读展示，注明「由 T+ 端生成并回写」，本平台不产生凭证数据。
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Result,
  Row,
  Space,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import {
  AuditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  FileProtectOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SendOutlined,
  StopOutlined,
  UploadOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import {
  CHECK_STATUS_LABELS,
  CheckStatus,
  FlowAction,
  FlowTaskStatus,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  INVOICE_VALIDITY_LABELS,
  InvoiceStatus,
  InvoiceValidity,
  PERMISSION_BY_CODE,
  PermissionCode,
  ProviderMode,
  RISK_ITEM_LABELS,
  RiskLevel,
  TPLUS_SYNC_STATUS_LABELS,
  shouldBlockTplusPush,
  summarizeRiskLevel,
  type CheckReport,
  type InvoiceDetail as InvoiceDetailType,
  type OcrFields,
  type RiskItem,
} from '@ilc/shared';
import { invoiceApi, flowApi } from '@/api/endpoints';
import { mockCheckReport, mockInvoiceDetail } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';
import { PageHeader } from '@/components/PageHeader';
import { PageState, FallbackNotice, ErrorState } from '@/components/PageState';
import { RiskTag } from '@/components/RiskTag';
import { InvoiceStatusTag } from '@/components/InvoiceStatusTag';
import { CheckStatusTag } from '@/components/CheckStatusTag';
import { ProviderModeTag, isUntrustedMode } from '@/components/ProviderModeTag';
import { MaskedField } from '@/components/MaskedField';
import { Money } from '@/components/Money';
import { ScopeMatchTag } from '@/components/ScopeMatchTag';
import { ConfirmDanger } from '@/components/ConfirmDanger';

/** OCR 字段中文名（与票面用词一致，方便对照原件） */
const OCR_FIELD_LABELS: Record<keyof OcrFields, string> = {
  invoiceCode: '发票代码',
  invoiceNumber: '发票号码',
  invoiceDate: '开票日期',
  machineCode: '机器编号',
  checkCode: '校验码',
  passwordArea: '密码区',
  invoiceType: '发票种类',
  purchaserName: '购方名称',
  purchaserTaxNumber: '购方税号',
  purchaserContactInfo: '购方地址电话',
  purchaserBankAccountInfo: '购方开户行及账号',
  sellerName: '销方名称',
  sellerTaxNumber: '销方税号',
  sellerContactInfo: '销方地址电话',
  sellerBankAccountInfo: '销方开户行及账号',
  invoiceAmountPreTax: '金额（不含税）',
  invoiceTax: '税额',
  totalAmount: '价税合计',
  totalAmountInWords: '价税合计（大写）',
  recipient: '收款人',
  reviewer: '复核人',
  drawer: '开票人',
  remarks: '备注',
  title: '抬头',
  details: '明细行',
};

/** 参与「票面 vs 税务侧」交叉核对的字段 —— 税务侧字段名沿用语税接口的命名 */
const TAX_COMPARE_FIELDS: Array<{
  ocr: keyof OcrFields;
  taxKeys: string[];
  label: string;
}> = [
  { ocr: 'invoiceCode', taxKeys: ['InvoiceCode', 'invoiceCode'], label: '发票代码' },
  { ocr: 'invoiceNumber', taxKeys: ['InvoiceNo', 'invoiceNo'], label: '发票号码' },
  { ocr: 'invoiceDate', taxKeys: ['InvoiceDate', 'invoiceDate'], label: '开票日期' },
  { ocr: 'sellerName', taxKeys: ['SalerName', 'SellerName', 'salerName'], label: '销方名称' },
  { ocr: 'sellerTaxNumber', taxKeys: ['SalerTaxNum', 'SellerTaxNo', 'salerTaxNum'], label: '销方税号' },
  { ocr: 'totalAmount', taxKeys: ['TotalAmount', 'totalAmount'], label: '价税合计' },
  { ocr: 'invoiceTax', taxKeys: ['TaxAmount', 'invoiceTax'], label: '税额' },
];

function pickTaxField(taxFields: Record<string, unknown> | null, keys: string[]): string | null {
  if (!taxFields) return null;
  for (const key of keys) {
    const value = taxFields[key];
    if (value !== undefined && value !== null && value !== '') return String(value);
  }
  return null;
}

/** 宽松比较：忽略空格、全半角、千分位与金额小数尾零 */
function looseEqual(a: string | null, b: string | null): boolean {
  if (!a || !b) return true;
  const norm = (v: string): string =>
    v
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/[\s,，\-—/年月日]/g, '')
      .trim();
  const na = norm(a);
  const nb = norm(b);
  if (na === nb) return true;
  const fa = Number(na);
  const fb = Number(nb);
  if (Number.isFinite(fa) && Number.isFinite(fb)) return Math.abs(fa - fb) < 0.005;
  return false;
}

/** 票面预览 —— 尽量还原纸质票的阅读顺序，便于与原件逐格核对 */
function TicketPreview({ detail }: { detail: InvoiceDetailType }): React.ReactElement {
  const ocr = detail.ocr?.fields;
  const lines = ocr?.details ?? [];

  const cell = (label: string, value: React.ReactNode): React.ReactElement => (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{label}</div>
      <div style={{ wordBreak: 'break-all' }}>{value}</div>
    </div>
  );

  return (
    <div className="ilc-ticket-preview">
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {INVOICE_TYPE_LABELS[detail.invoiceType] ?? detail.invoiceType}
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {ocr?.invoiceType ?? '票种信息取自 OCR 识别结果'}
        </Typography.Text>
      </div>

      <Row gutter={[8, 8]}>
        <Col span={12}>{cell('发票代码', ocr?.invoiceCode ?? <Typography.Text type="secondary">（数电票无代码）</Typography.Text>)}</Col>
        <Col span={12}>{cell('发票号码', <span className="ilc-mono">{ocr?.invoiceNumber ?? detail.invoiceNo}</span>)}</Col>
        <Col span={12}>{cell('开票日期', <span className="ilc-mono">{ocr?.invoiceDate ?? detail.invoiceDate ?? '-'}</span>)}</Col>
        <Col span={12}>{cell('校验码', <span className="ilc-mono">{ocr?.checkCode ?? '-'}</span>)}</Col>
      </Row>

      <Divider style={{ margin: '10px 0' }} />

      <Row gutter={[8, 8]}>
        <Col span={12}>
          {cell(
            '购买方名称',
            ocr?.purchaserName ?? detail.buyerName ?? '-',
          )}
        </Col>
        <Col span={12}>
          {cell(
            '购买方纳税人识别号',
            <MaskedField value={ocr?.purchaserTaxNumber ?? detail.buyerTaxNo} kind="taxNo" permission={PermissionCode.INVOICE_READ} />,
          )}
        </Col>
        <Col span={12}>
          {cell(
            '销售方名称',
            ocr?.sellerName ?? detail.sellerName ?? '-',
          )}
        </Col>
        <Col span={12}>
          {cell(
            '销售方纳税人识别号',
            <MaskedField value={ocr?.sellerTaxNumber ?? detail.sellerTaxNo} kind="taxNo" permission={PermissionCode.INVOICE_READ} />,
          )}
        </Col>
      </Row>

      <Divider style={{ margin: '10px 0' }} />

      {(lines.length > 0 ? lines : []).map((line, index) => (
        <Row key={`${line.itemName ?? 'line'}-${index}`} gutter={[8, 4]}>
          <Col span={10}>{cell('货物或应税劳务名称', line.itemName ?? '-')}</Col>
          <Col span={5}>{cell('数量', line.quantity ?? '-')}</Col>
          <Col span={5}>{cell('单价', <span className="ilc-mono">{line.unitPrice ?? '-'}</span>)}</Col>
          <Col span={4}>{cell('税率', line.taxRate ?? '-')}</Col>
        </Row>
      ))}
      {lines.length === 0 ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          未识别到明细行（明细行不影响查验，但影响合同品类比对）
        </Typography.Text>
      ) : null}

      <Divider style={{ margin: '10px 0' }} />

      <Row gutter={[8, 8]}>
        <Col span={8}>{cell('金额（不含税）', <span className="ilc-mono">{ocr?.invoiceAmountPreTax ?? '待识别'}</span>)}</Col>
        <Col span={8}>{cell('税额', <span className="ilc-mono">{ocr?.invoiceTax ?? '待识别'}</span>)}</Col>
        <Col span={8}>
          {cell(
            '价税合计',
            <Money cents={detail.totalCents} strong withChinese />,
          )}
        </Col>
        <Col span={24}>
          {cell('价税合计（大写）', <span>{ocr?.totalAmountInWords ?? detail.totalInWords ?? '-'}</span>)}
        </Col>
      </Row>

      <Divider style={{ margin: '10px 0' }} />

      <Row gutter={[8, 8]}>
        <Col span={8}>{cell('收款人', ocr?.recipient ?? '-')}</Col>
        <Col span={8}>{cell('复核人', ocr?.reviewer ?? '-')}</Col>
        <Col span={8}>{cell('开票人', ocr?.drawer ?? '-')}</Col>
        <Col span={24}>{cell('备注', ocr?.remarks ?? '-')}</Col>
      </Row>

      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #d9d9d9', fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
        原件：{detail.fileName}（{(detail.fileSize / 1024).toFixed(0)} KB，{detail.fileMime}）· 文件哈希{' '}
        <span className="ilc-mono">{detail.fileHash.slice(0, 16)}…</span>（用于重复上传去重）
      </div>
    </div>
  );
}

/** OCR 人工修正弹窗 —— 必须填写修正原因，逐字段留痕 */
function OcrEditModal({
  open,
  detail,
  onClose,
  onSaved,
  onLocalSave,
}: {
  open: boolean;
  detail: InvoiceDetailType;
  onClose: () => void;
  onSaved: (next: InvoiceDetailType) => void;
  onLocalSave: (fields: Partial<Record<keyof OcrFields, string>>, reason: string) => void;
}): React.ReactElement {
  const [form] = Form.useForm<Record<string, string>>();
  const [saving, setSaving] = useState(false);
  const ocr = detail.ocr;

  const editableKeys: Array<keyof OcrFields> = [
    'invoiceCode',
    'invoiceNumber',
    'invoiceDate',
    'checkCode',
    'sellerName',
    'sellerTaxNumber',
    'purchaserName',
    'purchaserTaxNumber',
    'invoiceAmountPreTax',
    'invoiceTax',
    'totalAmount',
    'remarks',
  ];

  const initial = useMemo(() => {
    const values: Record<string, string> = {};
    for (const key of editableKeys) {
      const raw = ocr?.fields[key];
      values[key] = raw === null || raw === undefined ? '' : String(raw);
    }
    values.reason = '';
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocr, open]);

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    const reason = String(values.reason ?? '').trim();
    if (reason.length < 8) {
      feedback.warning('修正原因至少 8 个字，将写入操作日志供审计追溯');
      return;
    }
    const changed: Record<string, string | null> = {};
    for (const key of editableKeys) {
      const next = values[key] ?? '';
      const before = ocr?.fields[key];
      if ((before ?? '') !== next) changed[key] = next === '' ? null : next;
    }
    if (Object.keys(changed).length === 0) {
      feedback.info('没有检测到修改内容');
      return;
    }

    setSaving(true);
    const result = await performAction({
      action: () => invoiceApi.updateOcr(detail.id, { fields: changed, reason }),
      successMessage: `已修正 ${Object.keys(changed).length} 个字段，修改记录已留痕`,
      offline: () => onLocalSave(changed as Partial<Record<keyof OcrFields, string>>, reason),
      offlineMessage:
        '后端 OCR 修正接口尚未就绪，本次修改仅在本地演示展示，未写入数据库。修正原因已记录在页面留痕区。',
    });
    setSaving(false);
    if (result.ok && result.data) onSaved(result.data);
    if (result.ok) onClose();
  };

  return (
    <Modal
      open={open}
      title="人工修正 OCR 识别结果"
      width={760}
      onCancel={onClose}
      okText="保存修正"
      cancelText="取消"
      confirmLoading={saving}
      onOk={() => void submit()}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="修正会覆盖识别结果并写入审计日志"
        description="OCR 只是辅助手段，票面文字仍以原件为准。修正原因必填，逐字段记录「修改前 / 修改后 / 操作人 / 时间」，财务初审与事后抽查均可追溯。"
      />
      <Form form={form} layout="vertical" initialValues={initial} preserve={false}>
        <Row gutter={12}>
          {editableKeys.map((key) => (
            <Col xs={24} md={12} key={key}>
              <Form.Item
                name={key}
                label={
                  <Space size={4}>
                    {OCR_FIELD_LABELS[key]}
                    {ocr && ocr.confidence[key] !== undefined ? (
                      <Tag
                        color={(ocr.confidence[key] ?? 100) < 80 ? 'orange' : 'default'}
                        style={{ marginInlineStart: 4 }}
                      >
                        置信度 {ocr.confidence[key]}
                      </Tag>
                    ) : null}
                  </Space>
                }
              >
                <Input className="ilc-mono" />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Form.Item
          name="reason"
          label="修正原因（必填，不少于 8 字）"
          rules={[{ required: true, message: '请填写修正原因' }]}
        >
          <Input.TextArea rows={2} placeholder="如：票面「0」被识别为字母「O」，按原件修正销方税号" />
        </Form.Item>
      </Form>

      {(ocr?.editHistory ?? []).length > 0 ? (
        <>
          <Divider orientation="left" plain style={{ marginTop: 0 }}>
            历史修正记录（{ocr?.editHistory.length} 条）
          </Divider>
          <Table
            size="small"
            rowKey={(_r, index) => String(index)}
            pagination={false}
            dataSource={ocr?.editHistory ?? []}
            columns={[
              { title: '字段', dataIndex: 'field', width: 120, render: (v: string) => OCR_FIELD_LABELS[v as keyof OcrFields] ?? v },
              { title: '修正前', dataIndex: 'before', width: 150, render: (v: string | null) => <span className="ilc-mono">{v ?? '-'}</span> },
              { title: '修正后', dataIndex: 'after', width: 150, render: (v: string | null) => <span className="ilc-mono">{v ?? '-'}</span> },
              { title: '操作人', dataIndex: 'operatorName', width: 90 },
              { title: '时间', dataIndex: 'at', width: 150, render: (v: string) => <span className="ilc-mono" style={{ fontSize: 12 }}>{v.replace('T', ' ').slice(0, 16)}</span> },
              { title: '原因', dataIndex: 'reason', ellipsis: true },
            ]}
          />
        </>
      ) : null}
    </Modal>
  );
}

/** 人工核验接管弹窗（文档七）—— 外部校验不可用时的唯一出口 */
function ManualOverrideModal({
  open,
  unavailableItems,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  unavailableItems: Array<{ key: string; label: string }>;
  onClose: () => void;
  onSubmit: (reason: string, items: string[]) => void | Promise<void>;
  loading: boolean;
}): React.ReactElement {
  const [form] = Form.useForm<{ reason: string; items: string[] }>();
  return (
    <Modal
      open={open}
      title="人工核验并接管"
      width={620}
      onCancel={onClose}
      okText="确认接管并留痕"
      cancelText="取消"
      confirmLoading={loading}
      onOk={() => {
        void form.validateFields().then((values) => onSubmit(values.reason, values.items));
      }}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="接管不会把校验结果改成「成功」"
        description="接管只是给流程一个继续走的下游出口：本张发票会记录为「人工接管」，核验说明与勾选项一并写入操作日志。若后续外部接口恢复，仍可点「重试校验」拿回真实结论。"
      />
      <Form form={form} layout="vertical" initialValues={{ items: unavailableItems.map((i) => i.key) }}>
        <Form.Item label="不可用的校验项">
          <Space wrap>
            {unavailableItems.map((item) => (
              <Tag key={item.key} color="orange">
                {item.label}
              </Tag>
            ))}
          </Space>
        </Form.Item>
        <Form.Item
          name="items"
          label="我已人工核验的项（至少选一项）"
          rules={[{ required: true, message: '请勾选已人工核验的项' }]}
        >
          <Checkbox.Group
            options={unavailableItems.map((i) => ({ label: i.label, value: i.key }))}
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="核验说明（必填，不少于 10 字）"
          rules={[
            { required: true, message: '请填写核验说明' },
            { min: 10, message: '核验说明至少 10 个字' },
          ]}
          extra="写明「在哪里、用什么方式核验了什么、结论是什么」，这是事后抽查与外部审计的主要依据"
        >
          <Input.TextArea
            rows={4}
            placeholder="如：已登录国家税务总局全国增值税发票查验平台人工输入发票代码、号码、开票日期与校验码，查验结果为「一致」，票面信息与原件相符。"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function InvoiceDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuthStore((s) => s.auth);
  const can = useAuthStore((s) => s.can);
  const canApproveAmount = useAuthStore((s) => s.canApproveAmount);
  const canReleaseRisk = useAuthStore((s) => s.canReleaseRisk);

  const [ocrModalOpen, setOcrModalOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const detail = useResource(() => invoiceApi.detail(id ?? ''), {
    fallback: () => mockInvoiceDetail(id ?? ''),
    deps: [id],
    enabled: Boolean(id),
  });

  const report = useResource<CheckReport>(() => invoiceApi.report(id ?? ''), {
    fallback: () => mockCheckReport(id ?? ''),
    deps: [id],
    enabled: Boolean(id),
  });

  const data = detail.data;

  const riskItems: RiskItem[] = data?.riskItems ?? [];
  const blockInfo = shouldBlockTplusPush(riskItems);
  const overallRisk = data ? summarizeRiskLevel(riskItems) : RiskLevel.NONE;

  /**
   * 从编排日志里取出每一步的运行模式。
   *
   * 报告的另一半（状态）来自 `data.check.*Status`，而"用的是真实通道还是沙箱"
   * 只在 `orchestrationLog` 里。两者按 step 名对上。
   * 找不到就是历史数据（该字段上线前入库的），交给组件渲染成「通道未记录」。
   */
  const stepModeOf = (
    step: string,
  ): { mode: ProviderMode | null | undefined; impl: string | null | undefined } => {
    const found = (data?.check?.orchestrationLog ?? []).find((s) => s.step === step);
    return { mode: found?.mode, impl: found?.impl };
  };

  const checkStatuses: Array<{
    key: string;
    label: string;
    status: CheckStatus | null;
    mode: ProviderMode | null | undefined;
    impl: string | null | undefined;
  }> = data
    ? [
        { key: 'OCR', label: 'OCR 识别', status: data.check?.ocrStatus ?? null, ...stepModeOf('OCR') },
        {
          key: 'SUPPLIER_CHECK',
          label: '企查查主体校验',
          status: data.check?.supplierStatus ?? null,
          ...stepModeOf('SUPPLIER_CHECK'),
        },
        {
          key: 'TAX_CHECK',
          label: '税务局验真',
          status: data.check?.taxStatus ?? null,
          ...stepModeOf('TAX_CHECK'),
        },
        {
          key: 'CONTRACT_MATCH',
          label: '合同比对',
          status: data.check?.contractStatus ?? null,
          ...stepModeOf('CONTRACT_MATCH'),
        },
      ]
    : [];

  /** 需要人工接管的校验项：接口不可用或执行失败 */
  const unavailableItems = checkStatuses.filter(
    (item) => item.status === CheckStatus.UNAVAILABLE || item.status === CheckStatus.FAILED,
  );

  /**
   * 未走真实外部通道的校验项（沙箱模拟 / 通道未启用）。
   *
   * 与 `unavailableItems` 不同：那些是"跑了但失败"，这些是"跑完了但结论
   * 不是真实校验得来的"。后者更隐蔽——它显示的是绿色的「校验成功」。
   */
  const untrustedItems = checkStatuses.filter((item) => isUntrustedMode(item.mode));

  const pendingTask = (data?.flowInstance?.tasks ?? []).find((t) => t.status === FlowTaskStatus.PENDING);

  const taxCompareRows = TAX_COMPARE_FIELDS.map((field) => {
    const ocrValue = data?.ocr ? (data.ocr.fields[field.ocr] as string | null) : null;
    const taxValue = pickTaxField(data?.taxCheck?.taxFields ?? null, field.taxKeys);
    return {
      key: field.ocr,
      label: field.label,
      ocrValue,
      taxValue,
      mismatched: data?.taxCheck?.mismatchedFields?.includes(field.label) ?? false,
      equal: looseEqual(ocrValue, taxValue),
      confidence: data?.ocr?.confidence[field.ocr] ?? null,
    };
  });

  if (!id) {
    return (
      <Result
        status="404"
        title="缺少发票 ID"
        extra={<Button type="primary" onClick={() => navigate('/business/invoices')}>返回列表</Button>}
      />
    );
  }

  if (detail.loading && !data) {
    return (
      <div className="ilc-page">
        <PageState loading skeleton rows={8}>
          {null}
        </PageState>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ilc-page">
        <ErrorState error={detail.error} onRetry={detail.reload} title="发票加载失败" />
        <Space style={{ marginTop: 12 }}>
          <Button onClick={() => navigate('/business/invoices')}>返回列表</Button>
          <Button type="primary" onClick={detail.reload}>
            重试
          </Button>
        </Space>
      </div>
    );
  }

  const approvedAmountOk = canApproveAmount(data.totalCents ?? 0);
  const riskOk = canReleaseRisk(overallRisk);
  const canReview = can(PermissionCode.INVOICE_REVIEW);
  const canPush = can(PermissionCode.TPLUS_PUSH);

  /**
   * 初审操作区：**放在页面最上面**。
   *
   * 【为什么要挪上来】这组按钮原先在「流程流转与操作留痕」卡片底部，
   * 而那张卡片排在票面预览、四张校验卡、风险清单之后——初审岗从待办
   * 点「审阅并处理」进来，要滚过整整一屏多才找得到"我该点哪里"，
   * 于是很自然地反问"财务初审到底做什么"。
   * 待办跳转过来的人，第一眼就该看见属于他的那个动作。
   *
   * 【顺带说清"什么算通过"】按钮旁边一句白话：通过=票面与校验结果一致，
   * 发票转入推送 T+ 节点；退回=回到经办人手里修改。这不只是文案——
   * 系统确实只做风险标记，放行判断始终由人做（见技术开发文档第十一节）。
   */
  const reviewActionCard =
    pendingTask && canReview ? (
      <Card
        size="small"
        style={{ marginBottom: 12, borderColor: '#adc6ff', background: '#f8fbff' }}
        title={
          <Space>
            <AuditOutlined style={{ color: '#1677ff' }} />
            <span>财务初审：本张发票待你处理</span>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            核对下方校验报告与风险项：<Typography.Text strong>票面与校验结果一致</Typography.Text>
            就点「初审通过」（发票转入推送 T+ 节点）；
            <Typography.Text strong>有问题</Typography.Text>
            就点「退回经办人」并写明原因（发票回到经办人手里修改后重新提交）。
            系统只做风险标记，放行与否始终由你判断。
          </Typography.Text>
          {!approvedAmountOk ? (
            <Alert
              type="warning"
              showIcon
              message={`本张发票价税合计 ${(data.totalCents ?? 0) / 100} 元，超出您的审核金额上限，请转交更高权限人员`}
            />
          ) : null}
          {!riskOk ? (
            <Alert
              type="warning"
              showIcon
              message={`本张发票风险等级为 ${overallRisk}，超出您可放行的范围，需走特例放行`}
            />
          ) : null}
          <Space wrap>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={busy}
              disabled={!approvedAmountOk || !riskOk}
              onClick={() => void handleApprove()}
            >
              初审通过
            </Button>
            <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectOpen(true)}>
              退回经办人
            </Button>
            <Button type="link" onClick={() => navigate(`/business/invoices/${data.id}/report`)}>
              先看校验报告
            </Button>
          </Space>
        </Space>
      </Card>
    ) : null;

  const handleLocalOcr = (fields: Partial<Record<keyof OcrFields, string>>, reason: string): void => {
    detail.setData((prev) => {
      if (!prev || !prev.ocr) return prev;
      const history = [...prev.ocr.editHistory];
      for (const [key, value] of Object.entries(fields)) {
        history.push({
          field: key,
          before: (prev.ocr.fields[key as keyof OcrFields] as string | null) ?? null,
          after: value ?? null,
          operatorId: auth?.userId ?? 'local',
          operatorName: auth?.name ?? '本机演示',
          at: new Date().toISOString(),
          reason,
        });
      }
      return {
        ...prev,
        ocr: {
          ...prev.ocr,
          fields: { ...prev.ocr.fields, ...(fields as Partial<OcrFields>) },
          manuallyEdited: true,
          editedBy: auth?.userId ?? 'local',
          editedAt: new Date().toISOString(),
          editHistory: history,
        },
      };
    });
  };

  const handleSubmit = async (): Promise<void> => {
    setBusy(true);
    await performAction({
      action: () => invoiceApi.submit(data.id, {}),
      successMessage: '已提交校验，校验结果将在完成后进入财务初审',
      offline: () =>
        detail.setData((prev) =>
          prev ? { ...prev, invoiceStatus: InvoiceStatus.CHECKING, submittedAt: new Date().toISOString() } : prev,
        ),
      offlineMessage: '后端提交接口尚未就绪，状态变更仅在本地演示',
    });
    setBusy(false);
  };

  const handleRetry = async (): Promise<void> => {
    setBusy(true);
    await performAction({
      action: () => invoiceApi.retryChecks(data.id, {}),
      successMessage: '已发起重试，稍后刷新查看结果',
      offline: () => feedback.info('后端重试接口尚未就绪，本次未真正发起重试'),
    });
    setBusy(false);
    detail.reload();
  };

  const handleApprove = async (): Promise<void> => {
    if (!approvedAmountOk) {
      feedback.error('本张发票金额超出您的审核上限，需由更高权限的财务人员处理');
      return;
    }
    if (!riskOk) {
      feedback.error('本张发票风险等级超出您可放行的范围，请走「特例放行」并填写专项审批依据');
      return;
    }
    setBusy(true);
    if (pendingTask) {
      await performAction({
        action: () => flowApi.handle(pendingTask.id, { action: FlowAction.APPROVE, comment: '初审通过，票面与校验结果一致' }),
        successMessage: '初审通过，已流转至下一节点',
        offline: () =>
          detail.setData((prev) =>
            prev ? { ...prev, invoiceStatus: InvoiceStatus.APPROVED, approvedAt: new Date().toISOString() } : prev,
          ),
        offlineMessage: '后端流程接口尚未就绪，状态流转仅在本地演示',
      });
    } else {
      await performAction({
        action: () => Promise.reject(new Error('当前没有待您处理的流程节点')),
        successMessage: '初审通过',
      });
    }
    setBusy(false);
  };

  const handleReject = async (reason: string): Promise<void> => {
    setBusy(true);
    const result = await performAction<unknown>({
      action: async () =>
        pendingTask
          ? flowApi.handle(pendingTask.id, { action: FlowAction.REJECT_TO_HANDLER, comment: reason })
          : invoiceApi.reject(data.id, { reason }),
      successMessage: '已退回经办人，退回原因已通知上传人',
      offline: () =>
        detail.setData((prev) =>
          prev
            ? {
                ...prev,
                invoiceStatus: InvoiceStatus.REJECTED,
                rejectCount: prev.rejectCount + 1,
                lastRejectReason: reason,
              }
            : prev,
        ),
      offlineMessage: '后端退回接口尚未就绪，状态变更与退回原因仅在本地演示',
    });
    setBusy(false);
    if (result.ok) setRejectOpen(false);
  };

  const handleSpecialRelease = async (reason: string): Promise<void> => {
    setBusy(true);
    const result = await performAction<unknown>({
      action: async () =>
        pendingTask
          ? flowApi.handle(pendingTask.id, {
              action: FlowAction.SPECIAL_RELEASE,
              comment: '高风险发票特例放行',
              specialReleaseReason: reason,
            })
          : invoiceApi.submit(data.id, { remark: `特例放行：${reason}` }),
      successMessage: '已完成特例放行，专项审批依据已写入操作日志',
      offline: () =>
        detail.setData((prev) => (prev ? { ...prev, invoiceStatus: InvoiceStatus.APPROVED } : prev)),
      offlineMessage: '后端流程接口尚未就绪，放行结果仅在本地演示，未产生真实审批留痕',
    });
    setBusy(false);
    if (result.ok) setReleaseOpen(false);
  };

  const handleManualOverride = async (reason: string, items: string[]): Promise<void> => {
    setBusy(true);
    const result = await performAction({
      action: () => invoiceApi.manualOverride(data.id, { reason, items }),
      successMessage: '已记录人工接管，业务可继续流转',
      offline: () => detail.reload(),
      offlineMessage: '后端人工接管接口尚未就绪，核验说明未写入数据库（本地已保留在弹窗留痕中）',
    });
    setBusy(false);
    if (result.ok) setOverrideOpen(false);
  };

  const handlePushTplus = async (): Promise<void> => {
    setBusy(true);
    const result = await performAction({
      action: () => invoiceApi.pushTplus(data.id, {}),
      successMessage: '已推送至 T+ 待办业务池，请财务在 T+ 端选择会计科目生成凭证',
      offline: () =>
        detail.setData((prev) => (prev ? { ...prev, invoiceStatus: InvoiceStatus.PUSHED } : prev)),
      offlineMessage: '后端推送接口尚未就绪，推送结果仅在本地演示',
    });
    setBusy(false);
    if (result.ok) detail.reload();
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title={
          <Space wrap>
            <span>{data.bizNo}</span>
            <InvoiceStatusTag status={data.invoiceStatus} />
            <RiskTag level={overallRisk} />
            <Tag color={data.validity === InvoiceValidity.VALID ? 'green' : 'red'}>
              {INVOICE_VALIDITY_LABELS[data.validity]}
            </Tag>
          </Space>
        }
        description={`${
          // 未提交的票还没识别过，票种必然是默认的"其他/未识别"。
          // 直接显示这个会让用户以为识别失败，改成如实的"待识别"。
          data.invoiceStatus === 'DRAFT'
            ? '待识别'
            : INVOICE_TYPE_LABELS[data.invoiceType]
        } · 经办人 ${data.handlerName ?? '-'} · 上传时间 ${
          data.createdAt ? data.createdAt.replace('T', ' ').slice(0, 16) : '-'
        }`}
        extra={
          <Space wrap>
            {/**
             * 【返回哪里要按角色分】这个详情页经办人和财务初审岗都会打开，
             * 而它从前一律返回 `/business/invoices`（经办人的「我的发票」）——
             * 初审岗从待办点进来，点「返回列表」就落进了经办人的列表，
             * 侧边栏与面包屑也都高亮「我的发票」，这正是"我怎么到了我的发票"的来源。
             * 有初审权限的角色回初审队列，其余回发票列表。
             */}
            <Button onClick={() => navigate(canReview ? '/business/review' : '/business/invoices')}>
              {canReview ? '返回初审队列' : '返回列表'}
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={() => navigate(`/business/invoices/${data.id}/report`)}
            >
              校验报告 / 打印
            </Button>
            {data.invoiceStatus === InvoiceStatus.DRAFT && can(PermissionCode.INVOICE_SUBMIT) ? (
              <Button type="primary" icon={<SendOutlined />} loading={busy} onClick={() => void handleSubmit()}>
                提交校验
              </Button>
            ) : null}
          </Space>
        }
      />

      {detail.isFallback ? (
        <FallbackNotice
          reason={detail.fallbackReason}
          onRetry={detail.reload}
          detail="发票详情、校验报告、风险项均为本地演示数据。接口就绪后本页无需改动即可展示真实结果。"
        />
      ) : null}

      {data.invoiceStatus === InvoiceStatus.REJECTED ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={`本张发票已被财务退回${data.rejectCount > 1 ? `（累计 ${data.rejectCount} 次）` : ''}`}
          description={
            <Space direction="vertical" size={2}>
              <span>退回原因：{data.lastRejectReason ?? '未填写'}</span>
              <span style={{ color: 'rgba(0,0,0,0.45)' }}>
                经办人可修正票面信息 / 补充合同后重新提交；退回原因与次数会记入操作日志。
              </span>
            </Space>
          }
        />
      ) : null}

      {unavailableItems.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          icon={<UserSwitchOutlined />}
          style={{ marginBottom: 12 }}
          message={`${unavailableItems.map((i) => i.label).join('、')} 暂不可用，需人工核验并接管`}
          description={
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <span>
                外部接口异常不影响业务继续：人工核验票面后填写核验说明即可接管，流程不会被卡死。
                接管记录会与发票一同归档，接口恢复后可再次重试校验。
              </span>
              <Space>
                <Button
                  type="primary"
                  danger
                  icon={<UserSwitchOutlined />}
                  disabled={!can(PermissionCode.CHECK_MANUAL_OVERRIDE)}
                  onClick={() => setOverrideOpen(true)}
                >
                  人工核验并接管
                </Button>
                <Button icon={<ReloadOutlined />} loading={busy} onClick={() => void handleRetry()}>
                  重试校验
                </Button>
                {!can(PermissionCode.CHECK_MANUAL_OVERRIDE) ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    您没有「{PERMISSION_BY_CODE[PermissionCode.CHECK_MANUAL_OVERRIDE]?.name ?? '外部校验人工接管'}」权限，请联系财务初审岗
                  </Typography.Text>
                ) : null}
              </Space>
            </Space>
          }
        />
      ) : null}

      {blockInfo.blocked ? (
        <Alert
          type="error"
          showIcon
          icon={<StopOutlined />}
          style={{ marginBottom: 12 }}
          message="高风险发票：默认禁止流转 T+"
          description={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {blockInfo.reasons.map((item) => (
                  <li key={item.code}>
                    <Tag color="red">{RISK_ITEM_LABELS[item.code] ?? item.code}</Tag>
                    {item.message}
                  </li>
                ))}
              </ul>
              <Space>
                <Button
                  danger
                  icon={<SafetyOutlined />}
                  disabled={!can(PermissionCode.INVOICE_SPECIAL_RELEASE)}
                  onClick={() => setReleaseOpen(true)}
                >
                  特例放行（需专项审批依据）
                </Button>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  特例放行不是「点一下就过」：必须填写专项审批依据，全程留痕，事后抽查重点核查。
                </Typography.Text>
              </Space>
            </Space>
          }
        />
      ) : null}

      {reviewActionCard}

      <Row gutter={[16, 16]}>
        {/* ——— 左：票面与字段对照 ——— */}
        <Col xs={24} xl={11}>
          <Card size="small" title="发票票面预览" extra={<Tag>按 OCR 识别结果还原</Tag>}>
            <TicketPreview detail={data} />
          </Card>

          <Card
            size="small"
            title="票面字段 vs 税务侧记录"
            style={{ marginTop: 16 }}
            extra={
              <Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  不一致字段：
                  {data.taxCheck?.mismatchedFields?.length ? (
                    <Typography.Text type="danger" style={{ fontSize: 12 }}>
                      {data.taxCheck.mismatchedFields.join('、')}
                    </Typography.Text>
                  ) : (
                    '无'
                  )}
                </Typography.Text>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={!can(PermissionCode.INVOICE_UPDATE_OCR)}
                  onClick={() => setOcrModalOpen(true)}
                >
                  人工修正
                </Button>
              </Space>
            }
          >
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={taxCompareRows}
              columns={[
                { title: '字段', dataIndex: 'label', width: 110 },
                {
                  title: '票面（OCR）',
                  dataIndex: 'ocrValue',
                  render: (v: string | null, row) => (
                    <Space size={4}>
                      <span className="ilc-mono" style={{ fontSize: 12 }}>
                        {v ?? '-'}
                      </span>
                      {row.confidence !== null && row.confidence < 80 ? (
                        <Tooltip title="识别置信度偏低，建议人工核对原件后修正">
                          <Tag color="orange">置信度 {row.confidence}</Tag>
                        </Tooltip>
                      ) : null}
                    </Space>
                  ),
                },
                {
                  title: '税务侧',
                  dataIndex: 'taxValue',
                  render: (v: string | null) => (
                    <span className="ilc-mono" style={{ fontSize: 12 }}>
                      {v ?? '—'}
                    </span>
                  ),
                },
                {
                  title: '核对',
                  key: 'result',
                  width: 90,
                  render: (_v, row) => {
                    if (row.mismatched) return <Tag color="orange">不一致</Tag>;
                    if (row.taxValue === null) return <Tag>无对照值</Tag>;
                    return row.equal ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        一致
                      </Tag>
                    ) : (
                      <Tag color="orange" icon={<CloseCircleOutlined />}>
                        不一致
                      </Tag>
                    );
                  },
                },
              ]}
            />
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              OCR 只是把票面转成文本，识别结果可能与原件有出入（常见：数字 0 与字母 O、税号位数）。
              不一致字段会进入风险项，由财务初审判断是识别误差还是票面本身有问题。人工修正需填写原因并留痕。
            </Typography.Paragraph>
          </Card>

          {data.check?.manualOverride ? (
            <Card size="small" title="人工接管记录" style={{ marginTop: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="接管人">
                  {data.check.manualOverride.operatorName}（{data.check.manualOverride.operatorId}）
                </Descriptions.Item>
                <Descriptions.Item label="接管时间">
                  {data.check.manualOverride.at.replace('T', ' ').slice(0, 16)}
                </Descriptions.Item>
                <Descriptions.Item label="核验项">
                  <Space wrap size={4}>
                    {data.check.manualOverride.items.map((item) => (
                      <Tag key={item} color="blue">
                        {item}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="核验说明">{data.check.manualOverride.reason}</Descriptions.Item>
              </Descriptions>
            </Card>
          ) : null}
        </Col>

        {/* ——— 右：校验报告 ——— */}
        <Col xs={24} xl={13}>
          <Card
            size="small"
            title={`校验报告 ${data.reportNo ?? data.check?.reportNo ?? '（尚未生成）'}`}
            extra={
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={() => navigate(`/business/invoices/${data.id}/report`)}
              >
                可打印报告
              </Button>
            }
          >
            <PageState loading={report.loading} error={report.error} onRetry={report.reload} skeleton rows={4}>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {/*
                  草稿状态时给出明确说明。
                  OCR 是校验编排的第一步，只在**提交校验之后**才执行——
                  上传完不做任何事是符合设计的（识别按次计费，不该在上传时就花钱）。
                  但界面上四个框显示"未知"，读起来像"识别失败了"，
                  用户会以为是 OCR 出问题，而实际是还没开始。
                */}
                {data.invoiceStatus === 'DRAFT' ? (
                  <Alert
                    type="info"
                    showIcon
                    message="本发票尚未提交校验，识别与校验都还没开始"
                    description={
                      <Space direction="vertical" size={2}>
                        <span>
                          点右上角的<strong>「提交校验」</strong>后，系统才会依次执行：
                          票面识别 → 供应商校验 → 发票验真 → 合同比对 → 风险评级。
                        </span>
                        <span style={{ color: 'rgba(0,0,0,0.45)' }}>
                          识别与外部查验都是按次计费的，因此不在上传时就自动跑——避免上传了却没用上的票也产生费用。
                        </span>
                      </Space>
                    }
                  />
                ) : null}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  校验完成时间：
                  {data.check?.checkedAt ? data.check.checkedAt.replace('T', ' ').slice(0, 19) : '-'} · 耗时{' '}
                  {data.check?.durationMs ?? '-'} ms · 报告编号可带入钉钉审批作为附件
                </Typography.Text>

                {/*
                  未走真实通道的提示。
                  「校验成功」四个字本身不区分数据来源——沙箱伪造的数据也是"成功"。
                  这条提示把"结论不是真实校验得来的"这件事摆在最显眼处，
                  避免它被四个绿勾盖过去。
                */}
                {untrustedItems.length > 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message={`本次校验有 ${untrustedItems.length} 项未通过真实外部通道`}
                    description={
                      <Space direction="vertical" size={2}>
                        <span>
                          {untrustedItems.map((i) => i.label).join('、')}
                          —— 这些项显示「成功」，但结论来自沙箱模拟或通道未启用，
                          <strong>不能直接作为业务凭据</strong>。
                        </span>
                        <span style={{ color: 'rgba(0,0,0,0.45)' }}>
                          每张卡片下方标注了本项的实际通道来源；法人签字前请确认关键项为「真实调用」。
                        </span>
                      </Space>
                    }
                  />
                ) : null}

                <Row gutter={[8, 8]}>
                  {checkStatuses.map((item) => (
                    <Col xs={12} key={item.key}>
                      <Card size="small" styles={{ body: { padding: 10 } }}>
                        <Space direction="vertical" size={2}>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {item.label}
                          </Typography.Text>
                          <Space size={4} wrap>
                            <CheckStatusTag status={item.status} itemName={item.label} />
                            <ProviderModeTag
                              mode={item.mode}
                              impl={item.impl}
                              itemName={item.label}
                            />
                          </Space>
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Space>
            </PageState>
          </Card>

          <Card
            size="small"
            title={`风险项清单（${riskItems.length} 项）`}
            style={{ marginTop: 16 }}
            extra={<RiskTag level={overallRisk} />}
          >
            <Table<RiskItem>
              size="small"
              rowKey="code"
              pagination={false}
              dataSource={riskItems}
              locale={{ emptyText: '未发现风险项' }}
              columns={[
                {
                  title: '风险项',
                  dataIndex: 'code',
                  width: 150,
                  render: (v: string) => <Tag>{RISK_ITEM_LABELS[v as keyof typeof RISK_ITEM_LABELS] ?? v}</Tag>,
                },
                {
                  title: '等级',
                  dataIndex: 'level',
                  width: 90,
                  render: (v: RiskLevel) => <RiskTag level={v} withHint={false} size="small" />,
                },
                { title: '说明与处置建议', dataIndex: 'message' },
                {
                  title: '证据',
                  dataIndex: 'evidence',
                  width: 90,
                  render: (v: Record<string, unknown> | undefined) =>
                    v && Object.keys(v).length > 0 ? (
                      <Tooltip
                        title={Object.entries(v)
                          .map(([k, val]) => `${k}: ${String(val)}`)
                          .join('；')}
                      >
                        <a>查看</a>
                      </Tooltip>
                    ) : (
                      <Typography.Text type="secondary">—</Typography.Text>
                    ),
                },
              ]}
            />
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              风险等级由风险项自动汇总（取最高级）。高风险（红冲 / 作废 / 供应商经营异常）默认阻断流转 T+；
              中风险（超经营范围、合同不一致等）只提示、不拦截，由财务初审判断后继续。
            </Typography.Paragraph>
          </Card>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card size="small" title="企查查主体校验">
                {data.supplierCheck ? (
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="企业名称">{data.supplierCheck.companyName ?? '-'}</Descriptions.Item>
                    <Descriptions.Item label="统一社会信用代码">
                      <MaskedField value={data.supplierCheck.creditCode} kind="taxNo" permission={PermissionCode.INVOICE_READ} />
                    </Descriptions.Item>
                    <Descriptions.Item label="经营状态">
                      <Tag color={data.supplierCheck.status === '存续' || data.supplierCheck.status === '在业' ? 'green' : 'red'}>
                        {data.supplierCheck.status ?? '未知'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="法定代表人">{data.supplierCheck.operName ?? '-'}</Descriptions.Item>
                    <Descriptions.Item label="注册资本">{data.supplierCheck.registCapi ?? '-'}</Descriptions.Item>
                    <Descriptions.Item label="经营范围匹配度">
                      <ScopeMatchTag
                        confidence={data.supplierCheck.scopeMatchConfidence}
                        matched={data.supplierCheck.scopeMatched}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="经营范围原文">
                      <Typography.Paragraph
                        type="secondary"
                        style={{ fontSize: 12, marginBottom: 0, maxHeight: 72, overflow: 'auto' }}
                      >
                        {data.supplierCheck.scope ?? '-'}
                      </Typography.Paragraph>
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚无主体校验结果" />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card size="small" title="税务局验真">
                {data.taxCheck ? (
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="查验结论">
                      <Tag color={data.taxCheck.validity === InvoiceValidity.VALID ? 'green' : 'red'}>
                        {INVOICE_VALIDITY_LABELS[data.taxCheck.validity]}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="查验通道">
                      <span className="ilc-mono">{data.taxCheck.channel}</span>
                      {data.taxCheck.channelCode ? (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {' '}
                          返回码 {data.taxCheck.channelCode}
                        </Typography.Text>
                      ) : null}
                    </Descriptions.Item>
                    <Descriptions.Item label="通道消息">{data.taxCheck.channelMessage ?? '-'}</Descriptions.Item>
                    <Descriptions.Item label="作废/红冲标志">
                      <span className="ilc-mono">{data.taxCheck.invalidMark ?? '-'}</span>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {' '}
                        （N 正常 / Y 红冲 / H 作废）
                      </Typography.Text>
                    </Descriptions.Item>
                    {data.taxCheck.blueInvoiceNo ? (
                      <Descriptions.Item label="对应蓝票">
                        <span className="ilc-mono">
                          {data.taxCheck.blueInvoiceCode ?? ''} {data.taxCheck.blueInvoiceNo}
                        </span>
                      </Descriptions.Item>
                    ) : null}
                    {/**
                     * 进项锁定状态。
                     *
                     * 【从前这里是一句做不到的承诺】原文写「由具备专项权限的人员在
                     * 『外部接口配置』中开启后执行」，而后端**根本没有执行路径**：
                     * 配置开关没人消费、适配器方法阿里云通道压根没有、tax_check 的
                     * 三个锁定列只读不写。用户打开开关后什么都不会发生，也无从知晓。
                     *
                     * 现在的口径是如实：当前通道（阿里云验真）没有税务侧写能力，
                     * 这一点直接写在界面上，并指明要换哪条通道；开关那个动作
                     * 也不再暗示"打开就会执行"（见 Providers.tsx 的说明与文案）。
                     */}
                    <Descriptions.Item label="进项锁定状态">
                      <Tag
                        color={
                          data.taxCheck.inputLockStatus === 'LOCKED'
                            ? 'green'
                            : data.taxCheck.inputLockStatus === 'FAILED'
                              ? 'red'
                              : 'default'
                        }
                      >
                        {data.taxCheck.inputLockStatus === 'LOCKED'
                          ? '已锁定'
                          : data.taxCheck.inputLockStatus === 'NOT_ATTEMPTED'
                            ? '未发起'
                            : data.taxCheck.inputLockStatus === 'UNSUPPORTED'
                              ? '当前通道不支持'
                              : data.taxCheck.inputLockStatus}
                      </Tag>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {' '}
                        当前验真通道（阿里云）只做查询核验，没有税务侧写能力，无法锁定进项。
                        如需在税务侧锁定 / 勾选抵扣，需改用乐企通道，或采购支持「进项发票勾选认证」
                        的服务商通道。
                      </Typography.Text>
                    </Descriptions.Item>
                    {data.taxCheck.errorMessage ? (
                      <Descriptions.Item label="异常信息">
                        <Typography.Text type="danger" style={{ fontSize: 12 }}>
                          {data.taxCheck.errorMessage}
                        </Typography.Text>
                      </Descriptions.Item>
                    ) : null}
                  </Descriptions>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚无验真结果" />
                )}
              </Card>
            </Col>
          </Row>

          <Card size="small" title="合同比对" style={{ marginTop: 16 }}>
            {/**
             * 【为什么要判 contractNo 而不只判 contractRef 是否存在】
             * 未关联合同的发票也会有一条 contract_ref 记录（校验时写下的
             * "跳过比对"），它的 contractNo 是空串。只判对象存在的话，
             * 面板会渲染成"关联合同：（空白）+ 校验成功 + 四项未比对"——
             * 2026-09-17 用户就是被这个组合看懵的。
             */}
            {data.contractRef?.contractNo ? (
              <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="关联合同">
                  <Link to="/business/legal">{data.contractRef.contractNo}</Link>
                </Descriptions.Item>
                <Descriptions.Item label="合同比对状态">
                  <CheckStatusTag status={data.contractRef.checkStatus} itemName="合同比对" />
                </Descriptions.Item>
                <Descriptions.Item label="金额是否超合同额度">
                  {data.contractRef.amountExceeded === null ? (
                    '未比对'
                  ) : data.contractRef.amountExceeded ? (
                    <Tag color="orange">超出</Tag>
                  ) : (
                    <Tag color="green">未超出</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="品类是否一致">
                  {data.contractRef.categoryMatched === null ? (
                    '未比对'
                  ) : data.contractRef.categoryMatched ? (
                    <Tag color="green">一致</Tag>
                  ) : (
                    <Tag color="orange">不一致</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="结算阶段">
                  {data.contractRef.stageMatched === null ? (
                    '未比对'
                  ) : data.contractRef.stageMatched ? (
                    <Tag color="green">匹配</Tag>
                  ) : (
                    <Tag color="orange">不匹配</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="差异说明">
                  {data.contractRef.mismatchDetails?.length ? (
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                      {data.contractRef.mismatchDetails.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  ) : (
                    '无'
                  )}
                </Descriptions.Item>
                {data.contractRef.checkStatus === CheckStatus.PENDING ? (
                  <Descriptions.Item span={2}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      已记录合同关联；发票完成识别后（提交校验时）会自动比对金额、品类与结算阶段
                    </Typography.Text>
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical" size={2}>
                    <span>未关联合同</span>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      未关联合同属于低风险提示，不阻断流程；补充关联后即可做金额与品类比对
                    </Typography.Text>
                  </Space>
                }
              />
            )}
          </Card>

          <Card size="small" title="流程流转与操作留痕" style={{ marginTop: 16 }}>
            {data.flowInstance ? (
              <>
                <Descriptions column={2} size="small" style={{ marginBottom: 8 }}>
                  <Descriptions.Item label="流程状态">
                    {data.flowInstance.status}
                  </Descriptions.Item>
                  <Descriptions.Item label="当前节点">
                    {data.flowInstance.currentNodeName ?? data.flowInstance.currentNodeKey ?? '-'}
                  </Descriptions.Item>
                </Descriptions>
                <Timeline
                  items={(data.flowInstance.tasks ?? []).map((task) => ({
                    color:
                      task.status === FlowTaskStatus.APPROVED
                        ? 'green'
                        : task.status === FlowTaskStatus.REJECTED
                          ? 'red'
                          : task.status === FlowTaskStatus.PENDING
                            ? 'blue'
                            : 'gray',
                    children: (
                      <Space direction="vertical" size={2}>
                        <Space wrap>
                          <Typography.Text strong>{task.nodeName}</Typography.Text>
                          <Tag
                            color={
                              task.status === FlowTaskStatus.APPROVED
                                ? 'green'
                                : task.status === FlowTaskStatus.REJECTED
                                  ? 'red'
                                  : task.status === FlowTaskStatus.PENDING
                                    ? 'blue'
                                    : 'default'
                            }
                          >
                            {task.status === FlowTaskStatus.PENDING ? '待处理' : task.status === FlowTaskStatus.APPROVED ? '已通过' : task.status === FlowTaskStatus.REJECTED ? '已退回' : task.status}
                          </Tag>
                          {task.isSpecialRelease ? <Tag color="red">特例放行</Tag> : null}
                          {task.assigneeRoleCode ? <Tag>{task.assigneeRoleCode}</Tag> : null}
                        </Space>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {task.handledAt ? `处理时间 ${task.handledAt.replace('T', ' ').slice(0, 16)}` : '等待处理中'}
                          {task.assigneeUserName ? ` · 处理人 ${task.assigneeUserName}` : ''}
                        </Typography.Text>
                        {task.comment ? (
                          <Typography.Text style={{ fontSize: 12 }}>意见：{task.comment}</Typography.Text>
                        ) : null}
                        {task.specialReleaseReason ? (
                          <Typography.Text type="danger" style={{ fontSize: 12 }}>
                            专项审批依据：{task.specialReleaseReason}
                          </Typography.Text>
                        ) : null}
                      </Space>
                    ),
                  }))}
                />
                {pendingTask && canReview ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {/* 操作按钮已提到页面顶部（见下方 reviewActionCard 的说明），
                        这里只留一句指引，避免同一组按钮在同页出现两次 */}
                    「初审通过」与「退回经办人」在页面顶部。
                  </Typography.Text>
                ) : null}
              </>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="流程实例尚未创建（提交校验后生成）" />
            )}
          </Card>

          <Card size="small" title="T+ 同步与凭证" style={{ marginTop: 16 }}>
            {data.tplus ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="推送状态">
                  <Tag color={data.tplus.status === 'BOOKED' ? 'green' : data.tplus.status === 'FAILED' ? 'red' : 'blue'}>
                    {TPLUS_SYNC_STATUS_LABELS[data.tplus.status] ?? data.tplus.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="T+ 账套">{data.tplus.accountSet ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="T+ 单据号">
                  <span className="ilc-mono">{data.tplus.tplusBillNo ?? '-'}</span>
                </Descriptions.Item>
                <Descriptions.Item label="推送时间">
                  {data.tplus.pushedAt ? data.tplus.pushedAt.replace('T', ' ').slice(0, 19) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="凭证编号">
                  {data.tplus.voucherNo ? (
                    <span className="ilc-mono">{data.tplus.voucherNo}</span>
                  ) : (
                    <Typography.Text type="secondary">尚未生成</Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="入账状态">{data.tplus.bookingStatus ?? '待 T+ 端记账'}</Descriptions.Item>
                {data.tplus.pushError ? (
                  <Descriptions.Item label="推送异常">
                    <Typography.Text type="danger">{data.tplus.pushError}</Typography.Text>
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未推送至 T+" />
            )}

            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="凭证由 T+ 端人工生成，本平台不产生凭证数据"
              description="推送只把发票业务数据放入 T+「待办业务池」。凭证编号、入账时间、入账状态三个字段仅接受 T+ 回写，平台侧只读——这是为了避免系统自动生成凭证带来的合规风险。"
            />

            <Space wrap style={{ marginTop: 12 }}>
              <Tooltip
                title={
                  blockInfo.blocked
                    ? '高风险发票默认阻断，需先完成特例放行'
                    : !canPush
                      ? '您没有 tplus:push 权限'
                      : data.invoiceStatus !== InvoiceStatus.APPROVED
                        ? '发票需处于「初审通过」状态才能推送'
                        : ''
                }
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={busy}
                  disabled={
                    blockInfo.blocked || !canPush || data.invoiceStatus !== InvoiceStatus.APPROVED
                  }
                  onClick={() => void handlePushTplus()}
                >
                  推送至 T+ 待办业务池
                </Button>
              </Tooltip>
              {data.invoiceStatus === InvoiceStatus.PUSHED || data.invoiceStatus === InvoiceStatus.BOOKED ? (
                <Tag color="green">
                  {INVOICE_STATUS_LABELS[data.invoiceStatus]} —— 请在 T+ 端选择会计科目生成凭证
                </Tag>
              ) : null}
            </Space>
          </Card>

          <Card size="small" title="校验编排日志" style={{ marginTop: 16 }}>
            <Table
              size="small"
              rowKey="step"
              pagination={false}
              dataSource={data.check?.orchestrationLog ?? []}
              locale={{ emptyText: '暂无可用的编排日志' }}
              columns={[
                {
                  title: '步骤',
                  dataIndex: 'step',
                  width: 150,
                  render: (v: string) =>
                    ({ OCR: 'OCR 识别', SUPPLIER_CHECK: '企查查校验', TAX_CHECK: '税务局验真', CONTRACT_MATCH: '合同比对', RISK_GRADING: '风险评级' })[v] ?? v,
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 140,
                  render: (v: CheckStatus) => <CheckStatusTag status={v} />,
                },
                {
                  title: '耗时',
                  dataIndex: 'durationMs',
                  width: 90,
                  render: (v: number | null) => (v === null || v === undefined ? '-' : `${v} ms`),
                },
                {
                  title: '缓存',
                  dataIndex: 'fromCache',
                  width: 70,
                  render: (v: boolean | undefined) => (v ? <Tag>命中</Tag> : <Tag>实时</Tag>),
                },
                { title: '消息', dataIndex: 'message', render: (v: string | null) => v ?? '—' },
              ]}
            />
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              这一步一步的日志用于回答「为什么这张票没有校验结果」——是接口报错、配额用尽，还是走了缓存。
            </Typography.Paragraph>
          </Card>

          {unavailableItems.length > 0 && !data.check?.manualOverride ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message="提醒：本张发票仍需人工核验接管，否则流程不会继续"
              description={`请点击页面上方的「人工核验并接管」按钮，填写核验说明（${CHECK_STATUS_LABELS[CheckStatus.UNAVAILABLE]}不代表发票有问题，只代表这次没能查成）。`}
            />
          ) : null}
        </Col>
      </Row>

      <OcrEditModal
        open={ocrModalOpen}
        detail={data}
        onClose={() => setOcrModalOpen(false)}
        onSaved={detail.setData}
        onLocalSave={handleLocalOcr}
      />

      <ConfirmDanger
        open={rejectOpen}
        title="退回经办人"
        level="normal"
        confirmText="确认退回"
        loading={busy}
        description={
          <Space direction="vertical" size={4}>
            <span>退回后发票回到经办人手中，需修改票面信息或补充合同后重新提交。</span>
            <span>退回原因会展示给上传人，并计入退回次数（当前已退回 {data.rejectCount} 次）。</span>
          </Space>
        }
        reasonLabel="退回原因（必填，不少于 8 字，将通知上传人）"
        reasonPresets={[
          '销方税号与票面不一致，请核对原件后重新上传',
          '发票金额超出合同剩余额度，请补充合同或说明',
          '缺少合同关联，请关联合同后重新提交',
          '票面模糊无法识别关键字段，请上传清晰原件',
        ]}
        onConfirm={(reason) => handleReject(reason)}
        onCancel={() => setRejectOpen(false)}
      />

      <ConfirmDanger
        open={releaseOpen}
        title="特例放行（高风险发票）"
        level="high"
        confirmText="填写依据并放行"
        loading={busy}
        acknowledgeText="我已知晓：本操作将被审计抽查，若不符合税务合规要求由审批人承担责任"
        description={
          <Space direction="vertical" size={4}>
            <span>
              本张发票风险等级为 <Tag color="red">{overallRisk}</Tag>，默认禁止流转 T+。
            </span>
            <span>特例放行后发票可继续推送 T+，但放行依据会与发票一同归档，作为事后抽查与外部审计的重点核查项。</span>
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {blockInfo.reasons.map((item) => (
                <li key={item.code}>{item.message}</li>
              ))}
            </ul>
          </Space>
        }
        reasonLabel="专项审批依据（必填，不少于 15 字）：谁批准、依据什么、为什么必须入账"
        minReasonLength={15}
        reasonPresets={[
          '该发票为上月已认证进项，供应商误作废后已重新开具红票，经财务负责人批准按原票入账',
          '供应商已注销但货物真实入库且款项已支付，经总经理审批按实际业务入账',
        ]}
        onConfirm={(reason) => handleSpecialRelease(reason)}
        onCancel={() => setReleaseOpen(false)}
      />

      <ManualOverrideModal
        open={overrideOpen}
        unavailableItems={unavailableItems.length > 0 ? unavailableItems : checkStatuses}
        onClose={() => setOverrideOpen(false)}
        onSubmit={handleManualOverride}
        loading={busy}
      />

      {(() => {
        const ocrBadge = data.ocr ? `${data.ocr.overallConfidence}%` : '无';
        return (
          <div style={{ marginTop: 16 }}>
            <Card size="small" title="本页数据来源">
              <Space wrap size={[16, 8]}>
                <span>
                  <Badge status="processing" /> OCR 整体置信度：<span className="ilc-num">{ocrBadge}</span>
                </span>
                <span>
                  <Badge status="default" /> 识别通道：<span className="ilc-mono">{data.ocr?.engine ?? '-'}</span>
                </span>
                <span>
                  <Badge status={data.ocr?.manuallyEdited ? 'warning' : 'default'} /> 人工修正：
                  {data.ocr?.manuallyEdited ? `已修正（${data.ocr.editHistory.length} 处）` : '未修正'}
                </span>
                <span>
                  <Badge status="default" /> 验真通道：
                  <span className="ilc-mono">{data.taxCheck?.channel ?? '-'}</span>
                </span>
                <Button size="small" icon={<FileProtectOutlined />} onClick={() => navigate('/business/archive')}>
                  在档案库中追溯
                </Button>
              </Space>
            </Card>
          </div>
        );
      })()}
    </div>
  );
}
